import { Employee } from "@/types/global";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { employeeSelector, setPagination, setPinned, setSortBy } from "@/store/features/employeeSlice";
import { ColumnDef, flexRender, getCoreRowModel, useReactTable, ColumnResizeMode } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BsArrowUp } from "react-icons/bs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/common/Table/ItemsPerPage";
import { Link, useNavigate } from "react-router-dom";
import { H3, LightText2 } from "@/common/ui/Text";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { TABLE } from "@/utils/constant/table.constant";
import { BsArrowDown } from "react-icons/bs";
import { GrFormPin } from "react-icons/gr";
import { loginSelector } from "@/store/features/loginSlice";
import RenderColumn from "./RenderColumn";
import ButtonWithActions from "@/common/ui/ ButtonWithActions";
import { pinSelected } from "@/dmr/common/SvgIcons";
import { LOCALSTORAGE_KEY, PERMISSIONS, PINNED_LOCALSTORAGE_KEY } from "@/utils/constant/global.constant";
import { isHasPermission } from "@/utils/helper/permissions";
import Checkbox from "@/common/custom/Chekbox";
type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

interface AccessCounts {
  totalEmps: number;
  noWebaccess: number;
  hasWebaccess: number;
  exEmployes: number;
  disabledWebAccess: number;
  accountOwner: number;
}

interface TableViewProps {
  className?: string;
  employees: Employee[];
  selectedRows: string[];
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
  openModal: (modalKey: string, props?: any) => void;
  handleShareLinkClose?: () => void;
  rowSelection: any;
  setRowSelection: any;
  count?: number;
  onClickFetch: () => void;
  loading?: boolean;
  tableConfig?: any;
  setAccessCounts: SetState<AccessCounts>;
}

const TableView: React.FC<TableViewProps> = ({
  openModal,
  handleShareLinkClose,
  className = "",
  employees,
  rowSelection,
  setSelectedRows,
  setRowSelection,
  count,
  onClickFetch,
  loading = false,
  tableConfig,
  setAccessCounts,
}) => {
  const navigate = useNavigate();
  const { currentUser, currentOrg } = useSelector(loginSelector);
  const dispatch = useDispatch();
  const { pagination, sortBy, pinned } = useSelector(employeeSelector);
  const currentPage = pagination.pageNumber;
  const perPage = { value: pagination.pageSize, label: `Records per page ${pagination.pageSize}` };
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizeGuideline, setResizeGuideline] = useState({ show: false, x: 0 });

  const tableContainerRef = useRef<HTMLDivElement>(null);
  // console.log({ sortBy });
  useEffect(() => {
    const local = localStorage.getItem(`${currentOrg?.SignupOrg?.ext_org_id}${LOCALSTORAGE_KEY}`);
    if (local) {
      try {
        setColumnSizing(JSON.parse(local));
      } catch (err) {
        console.log(err);
      }
    }

    // Load pinned columns from localStorage
    const pinnedLocal = localStorage.getItem(`${currentOrg?.SignupOrg?.ext_org_id}${PINNED_LOCALSTORAGE_KEY}`);

    if (pinnedLocal) {
      try {
        const savedPinned = JSON.parse(pinnedLocal);
        if (Array.isArray(savedPinned)) {
          dispatch(setPinned(savedPinned));
        }
      } catch (err) {
        console.log(err);
      }
    }
  }, [currentOrg?.SignupOrg?.ext_org_id]);

  useEffect(() => {
    if (columnSizing && Object.keys(columnSizing).length > 0) {
      localStorage.setItem(`${currentOrg?.SignupOrg?.ext_org_id}${LOCALSTORAGE_KEY}`, JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  // Save pinned columns to localStorage whenever pinned state changes
  useEffect(() => {
    if (currentOrg?.SignupOrg?.ext_org_id) {
      if (pinned.length > 0) {
        localStorage.setItem(`${currentOrg?.SignupOrg?.ext_org_id}${PINNED_LOCALSTORAGE_KEY}`, JSON.stringify(pinned));
      } else {
        // Clear localStorage when no columns are pinned
        localStorage.removeItem(`${currentOrg?.SignupOrg?.ext_org_id}${PINNED_LOCALSTORAGE_KEY}`);
      }
    }
  }, [pinned, currentOrg?.SignupOrg?.ext_org_id]);

  useEffect(() => {
    const totalEmps = table.getSelectedRowModel().rows.length;
    const empExtIds: any = [];

    table.getSelectedRowModel().rows.some((r) => {
      if (r.original?.status?.label !== "Ex-employee" && r.original?.userTyp?.label !== "Account Owner") {
        empExtIds.push(r.original.empExtId);
        if (empExtIds.length >= 50) return true;
      }
      return false;
    });

    const hasWebAccess = table
      .getSelectedRowModel()
      .rows.filter((r) => r.original.webAccess == true && r.original?.status?.label != "Ex-employee" && r.original?.userTyp?.label != "Account Owner").length;
    const disabledWebAccess = table
      .getSelectedRowModel()
      .rows.filter((r) => r.original.webAccess == false && r.original?.status?.label != "Ex-employee" && r.original?.userTyp?.label != "Account Owner").length;
    const exEmployess = table.getSelectedRowModel().rows.filter((r) => r.original?.status?.label == "Ex-employee").length;
    const accountOwner = table.getSelectedRowModel().rows.filter((r) => r.original?.userTyp?.label == "Account Owner").length;

    setAccessCounts({
      totalEmps,
      noWebaccess: empExtIds.length,
      hasWebaccess: hasWebAccess,
      disabledWebAccess,
      accountOwner,
      exEmployes: exEmployess,
    });

    setSelectedRows((prev) => {
      const merged = new Set([...prev, ...empExtIds]);

      const currentPageIds = employees.map((e) => e.empExtId);
      const stillSelected = [...merged].filter((id) => !currentPageIds.includes(id) || empExtIds.includes(id));
      return stillSelected;
    });
  }, [rowSelection, employees]);

  // Filter columns by tableConfig.visibleFields
  const filteredFields = tableConfig?.visibleFields
    ? tableConfig.visibleFields.map((fieldId: string) => TABLE.EMPLOYEE_TABLE_FIELDS.find((col) => col.id === fieldId)).filter(Boolean)
    : TABLE.EMPLOYEE_TABLE_FIELDS;

  const getOrderedColumns = useCallback(() => {
    const selectCol: ColumnDef<Employee> = {
      id: "select",
      header: ({ table }) => {
        const allRowsSelected = table.getIsAllRowsSelected();
        const someRowsSelected = table.getIsSomeRowsSelected();

        return (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={allRowsSelected}
              onChange={() => table.toggleAllRowsSelected(!allRowsSelected)}
              className={someRowsSelected && !allRowsSelected ? "opacity-60" : ""}
            />
          </div>
        );
      },
      cell: ({ row }) => (
        <div className="flex items-center justify-end">
          <Checkbox checked={row.getIsSelected()} onChange={() => row.toggleSelected(!row.getIsSelected())} />
        </div>
      ),
      size: 75,
      minSize: 75,
      maxSize: 75,
      enableResizing: false,
    };
    const [firstField, secondField, ...otherFields] = filteredFields;
    const pinnedFields = otherFields.filter((col: any) => pinned.includes(col.id));
    const unpinnedFields = otherFields.filter((col: any) => !pinned.includes(col.id));
    const allFields = [firstField, secondField, ...pinnedFields, ...unpinnedFields];
    const mapped = allFields.map((tableColumn) => {
      const isPinned = pinned.includes(tableColumn.id);
      const isAnotherPinned = pinned.length > 0 && !isPinned;
      return {
        accessorKey: tableColumn.accessorKey,
        id: tableColumn.id,
        size: 190,
        minSize: 190,
        // NO maxSize (allow infinite right drag)
        enableResizing: true,
        header: () => {
          const sortInfo = sortBy.find((s: any) => s.field === tableColumn.id);
          const handleSortToggle = (isASC: boolean) => {
            if (sortInfo && sortInfo.isASC === isASC) {
              dispatch(setSortBy([]));
            } else {
              dispatch(setSortBy([{ field: tableColumn.id, isASC }]));
            }
          };
          const isActiveAsc = !!(sortInfo && sortInfo.isASC === true);
          const isActiveDesc = !!(sortInfo && sortInfo.isASC === false);
          const isDefaultPin = tableColumn.id === firstField?.id || tableColumn.id === secondField?.id;
          return (
            <div className="flex items-center group flex-1 justify-between relative">
              <H3 text={tableColumn.label} />
              <div className="flex items-center">
                {sortInfo ? sortInfo.isASC ? <BsArrowUp className="ml-1 text-text-primary" /> : <BsArrowDown className="ml-1 text-text-primary" /> : null}
                {isPinned ? pinSelected : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild className={tableConfig?.visibleFields?.[tableConfig.visibleFields.length - 1] === tableColumn.id ? "mr-10" : ""}>
                    <Button variant="ghost" className="h-6 w-6 p-0 invisible group-hover:visible">
                      <MdOutlineKeyboardArrowDown className="size-5 text-text-default" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handleSortToggle(true)}
                      className={`cursor-pointer px-3 py-1 text-13 text-text-default flex items-center gap-2 group hover:!bg-primaryOpacity-10 ${
                        isActiveAsc ? "!bg-primaryOpacity-10" : ""
                      }`}
                    >
                      <BsArrowDown className={`text-text-default group-hover:text-text-primary rotate-180 ${isActiveAsc ? "text-text-primary" : ""}`} />
                      <LightText2 text="Sort A - Z" className={`group-hover:text-text-primary ${isActiveAsc ? "text-text-primary" : ""}`} />
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleSortToggle(false)}
                      className={`cursor-pointer px-3 py-2 text-13 text-text-default group hover:!bg-primaryOpacity-10 flex items-center gap-2 ${
                        isActiveDesc ? "!bg-primaryOpacity-10" : ""
                      }`}
                    >
                      <BsArrowDown className={`text-text-default group-hover:text-text-primary ${isActiveDesc ? "text-text-primary" : ""}`} />
                      <LightText2 text="Sort Z - A" className={`group-hover:text-text-primary ${isActiveDesc ? "text-text-primary" : ""}`} />
                    </DropdownMenuItem>
                    {!isDefaultPin && (
                      <DropdownMenuItem
                        onClick={() => {
                          if (isPinned) {
                            dispatch(setPinned(pinned.filter((k) => k !== tableColumn.id)));
                          } else {
                            dispatch(setPinned([tableColumn.id]));
                          }
                        }}
                        disabled={isAnotherPinned && !isPinned}
                        className={`cursor-pointer px-3 py-2 text-13 text-text-default group hover:!bg-primaryOpacity-10 flex items-center gap-2 ${
                          isPinned ? "!bg-primaryOpacity-10" : ""
                        } ${isAnotherPinned && !isPinned ? "opacity-50 pointer-events-none" : ""}`}
                      >
                        <GrFormPin className={`text-text-default group-hover:text-text-primary ${isPinned ? "text-text-primary" : ""}`} />
                        <LightText2
                          text={isPinned ? "Unpin column" : "Pin column"}
                          className={`group-hover:text-text-primary ${isPinned ? "text-text-primary" : ""}`}
                        />
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        },
        cell: ({ row, column }: any) => <RenderColumn type={tableColumn?.viewType} row={row} column={column} />,
      };
    });
    return [selectCol, ...mapped];
  }, [pinned, sortBy, dispatch, filteredFields]);

  const columns: ColumnDef<Employee>[] = getOrderedColumns();

  const table = useReactTable({
    data: employees,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      sorting: sortBy.map((s: any) => ({ id: s.field, desc: !s.isASC })),
      rowSelection,
      columnSizing,
    },
    columnResizeMode: "onChange" as ColumnResizeMode,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: true,
    enableRowSelection: true,
  });

  const ResizeHandle = ({ header }: { header: any }) => {
    const handleMouseDown = (e: React.MouseEvent) => {
      setIsResizing(true);
      const containerRect = tableContainerRef.current?.getBoundingClientRect();
      // Find the header cell dom node for the column being resized
      const th = e.currentTarget.closest("th,td");
      const headerRect = th?.getBoundingClientRect();
      // Header left offset within the container (scrolled position is handled!)
      const headerLeftInContainer = (headerRect?.left || 0) - (containerRect?.left || 0);
      const minSize = header.column.columnDef.minSize ?? 100;
      const dragMinSize = minSize + 24;

      // Clamp initial X
      const xInContainer = Math.max(headerLeftInContainer + dragMinSize, e.clientX - (containerRect?.left || 0));
      setResizeGuideline({ show: true, x: xInContainer });

      const handleMouseMove = (moveEvent: MouseEvent) => {
        let x = moveEvent.clientX - (containerRect?.left || 0);
        const minX = headerLeftInContainer + dragMinSize;
        x = Math.max(minX, x); // clamp only left (min)
        setResizeGuideline({
          show: true,
          x,
        });
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        setResizeGuideline({ show: false, x: 0 });
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      header.getResizeHandler()(e);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
      setIsResizing(true);
      const containerRect = tableContainerRef.current?.getBoundingClientRect();
      const th = e.currentTarget.closest("th,td");
      const headerRect = th?.getBoundingClientRect();
      const headerLeftInContainer = (headerRect?.left || 0) - (containerRect?.left || 0);
      const minSize = header.column.columnDef.minSize ?? 100;
      const touch = e.touches[0];
      const xInContainer = Math.max(headerLeftInContainer + minSize, touch.clientX - (containerRect?.left || 0));
      setResizeGuideline({ show: true, x: xInContainer });

      const handleTouchMove = (moveEvent: TouchEvent) => {
        const touch = moveEvent.touches[0];
        let x = touch.clientX - (containerRect?.left || 0);
        const minX = headerLeftInContainer + minSize;
        x = Math.max(minX, x);
        setResizeGuideline({
          show: true,
          x,
        });
      };
      const handleTouchEnd = () => {
        setIsResizing(false);
        setResizeGuideline({ show: false, x: 0 });
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);

      header.getResizeHandler()(e);
    };

    return (
      <div
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className="absolute right-0 top-0 h-full w-2 bg-transparent hover:bg-blue-500/20 cursor-col-resize z-20 select-none"
        style={{ transform: "translateX(50%)" }}
      >
        <div className="absolute right-1/2 top-0 h-full w-0.5 bg-blue-500 opacity-0 group-hover:opacity-100" style={{ transform: "translateX(50%)" }} />
        <div className="absolute right-1/2 top-1/2 translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100">
          <div className="flex flex-col gap-0.5">
            <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <div className="rounded-md h-full justify-between shadow flex flex-col overflow-hidden relative">
        <div
          className={`overflow-hidden table-container flex-1 ${isResizing ? "cursor-col-resize select-none" : ""}`}
          ref={tableContainerRef}
          style={{ position: "relative" }}
        >
          {/* Guideline */}
          {resizeGuideline.show && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-blue-500 z-50 pointer-events-none"
              style={{
                left: resizeGuideline.x,
                minHeight: "100%",
                maxHeight: "100%",
                boxShadow: "0 0 8px 2px rgba(59, 130, 246, 0.25)",
              }}
            />
          )}

          <Table className={`flex-1 transition-colors duration-150 relative ${isResizing ? "select-none" : ""}`} style={{ width: table.getCenterTotalSize() }}>
            <TableHeader className="sticky top-0 z-20 bg-white whitespace-nowrap ">
              {!tableConfig ? (
                <TableRow className="animate-pulse">
                  {columns.map((col, colIdx) => (
                    <TableHead
                      key={col.id || colIdx}
                      className={
                        col.id === "select"
                          ? "pl-2 sticky left-0 bg-white border-b border-r border-stroke-light z-10 min-w-[56px] max-w-[56px] w-[56px]"
                          : "text-text-default whitespace-nowrap min-w-[200px] max-w-[200px] w-[200px] px-3 py-2"
                      }
                    >
                      <div className="h-4 px-3 py-[3px] w-full bg-gray-200 rounded" />
                    </TableHead>
                  ))}
                </TableRow>
              ) : (
                <>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className="hover:!bg-white relative">
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead
                            key={header.id}
                            className={`!p-0 relative transition-colors duration-150 ${header.column.id === "select" ? "sticky left-0 z-50 bg-white" : ""} ${
                              header.column.getIsResizing() ? "bg-blue-50/50" : ""
                            } ${isResizing ? "select-none" : ""}`}
                            style={{
                              width: header.getSize(),
                              minWidth: header.column.columnDef.minSize,
                            }}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={`h-full w-full ${
                                  header.id != "select" && "px-3"
                                } flex items-center border-r border-b border-stroke-light relative transition-colors duration-150 ${
                                  header.column.id === "select" ? "py-1 justify-end z-50" : "justify-between"
                                } ${header.column.getIsResizing() ? "bg-blue-50/30" : ""} ${isResizing ? "select-none" : ""}`}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanResize() && header.column.id !== "select" && <ResizeHandle header={header} />}
                              </div>
                            )}
                            
                          </TableHead>
                        );
                      })}
                        <div
                          id="settings"
                          className="sticky right-0 z-30 flex justify-end w-fit pointer-events-auto border-r border-stroke-light bg-white"
                        >
                          <ButtonWithActions
                            btnClassName="border-b border-stroke-light"
                            childOptions={[
                              {
                                name: "manage-column",
                                label: "Manage column",
                                onClick: () => openModal("manage-column"),
                                className: "flex items-center gap-2",
                              },
                              {
                                name: "reset-column-sizes",
                                label: "Reset column sizes",
                                onClick: () => {
                                  setColumnSizing({});
                                  table.resetColumnSizing();
                                  dispatch(setPinned([]));
                                  localStorage.removeItem(
                                    `${currentOrg?.SignupOrg?.ext_org_id}${LOCALSTORAGE_KEY}`
                                  );
                                },
                                className: "flex items-center gap-2",
                              },
                            ]}
                          />
                        </div>
                    </TableRow>
                  ))}
                </>
              )}
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 20 }).map((_, rowIdx) => (
                  <TableRow key={rowIdx} className="animate-pulse">
                    {columns.map((col, colIdx) => (
                      <TableCell
                        key={col.id || colIdx}
                        className={
                          col.id === "select"
                            ? "pl-2 sticky top-0 left-0 bg-white border-b border-r border-stroke-light z-10 min-w-[56px] max-w-[56px] w-[56px] !pr-0"
                            : "text-text-default whitespace-nowrap min-w-[200px] max-w-[200px] w-[200px] px-3 py-2"
                        }
                      >
                        <div className="h-4 px-3 py-[3px] w-full bg-gray-200 rounded" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row: any) => {
                  let empId: any = "";
                  const isLastRow = table.getRowModel().rows[table.getRowModel().rows.length - 1].id === row.id;
                  return (
                    <TableRow
                      key={row.id}
                      className={`group relative ${isLastRow ? "!border-b border-stroke-light" : ""}`}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell: any) => {
                        const isEmpId = cell.id?.includes("_empId");
                        if (isEmpId) {
                          empId = cell.getValue();
                        }
                        const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
                        const isAccountOwnerRole = row.original?.userTyp?.label === "Account Owner";
                        const isAccountOwner = currentUser?.role === "Account Owner";
                        const isLastCell = row.getVisibleCells()[row.getVisibleCells().length - 1].id === cell.id;

                        return (
                          <TableCell
                            key={cell.id}
                            className={`${
                              cell.column.id === "select"
                                ? "pl-2 sticky top-0 left-0 bg-white border-b border-r border-stroke-light z-10"
                                : "text-text-default whitespace-nowrap px-3 py-[3px] truncate"
                            } ${row.getIsSelected() ? "bg-primaryOpacity-solid10" : ""} ${cell.column.getIsResizing() ? "bg-blue-50/20" : ""} 
                            ${isLastCell ? "border-r" : ""}`}
                            style={{
                              width: cell.column.getSize(),
                              minWidth: cell.column.columnDef.minSize,
                            }}
                          >
                            {isEmpId && empId ? (
                              <Link to={`/home/employees/${row.original?.empExtId}/overview`} className="text-primary hover:underline text-text-link">
                                {cellContent}
                              </Link>
                            ) : (
                              cellContent
                            )}
                            {cell.column.id === "select" && (
                              <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                                {(() => {
                                  const dropdownOptions = [];
                                  if (
                                    isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["View Employees"]) ||
                                    isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["View Reporting Employee"])
                                  ) {
                                    dropdownOptions.push(
                                      <DropdownMenuItem
                                        key="view"
                                        onClick={() => (row.original?.empExtId ? navigate(`/home/employees/${row.original?.empExtId}/overview`) : null)}
                                        className="cursor-pointer px-3 pr-5 py-2 text-13 text-text-default data-[highlighted]:bg-primaryOpacity-10 data-[highlighted]:text-text-primary focus:bg-primaryOpacity-10 focus:text-text-primary"
                                      >
                                        View
                                      </DropdownMenuItem>
                                    );
                                  }
                                  if (
                                    (isAccountOwner ? isAccountOwner : !isAccountOwnerRole) &&
                                    isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Edit Employees"])
                                  ) {
                                    dropdownOptions.push(
                                      <DropdownMenuItem
                                        key="edit"
                                        onClick={() => {
                                          if (row.original) {
                                            openModal("edit", { afterCall: onClickFetch });
                                            navigate(`/home/employees/action/edit/${row.original?.empExtId}`, { replace: true });
                                          }
                                        }}
                                        className="cursor-pointer px-3 pr-5 py-2 text-13 text-text-default data-[highlighted]:bg-primaryOpacity-10 data-[highlighted]:text-text-primary focus:bg-primaryOpacity-10 focus:text-text-primary"
                                      >
                                        Edit
                                      </DropdownMenuItem>
                                    );
                                  }
                                  if (!isAccountOwnerRole && isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Delete Employees"])) {
                                    dropdownOptions.push(
                                      <DropdownMenuItem
                                        key="delete"
                                        onClick={() =>
                                          openModal("delete", {
                                            data: row.original,
                                            afterCall: onClickFetch,
                                          })
                                        }
                                        className="cursor-pointer px-3 pr-5 py-2 text-13 text-text-error data-[highlighted]:bg-primaryOpacity-10 data-[highlighted]:text-text-error focus:bg-primaryOpacity-10 focus:text-text-primary"
                                      >
                                        Delete
                                      </DropdownMenuItem>
                                    );
                                  }
                                  if (row.original?.status?.label !== "Ex-employee" && !isAccountOwnerRole) {
                                    dropdownOptions.push(
                                      <DropdownMenuItem
                                        key="mark-ex"
                                        className="cursor-pointer px-3 pr-5 py-2 text-13 text-text-default data-[highlighted]:bg-primaryOpacity-10 data-[highlighted]:text-text-primary focus:bg-primaryOpacity-10 focus:text-text-primary"
                                        onClick={() => {
                                          navigate(`/home/employees/action/exit-terminate/${row.original?.empExtId}`, { replace: true });
                                          openModal("exit-terminate", { data: row.original, afterCall: onClickFetch });
                                        }}
                                      >
                                        Mark as ex-employee
                                      </DropdownMenuItem>
                                    );
                                  }
                                  if (
                                    !isAccountOwnerRole &&
                                    row.original?.status?.label !== "Ex-employee" &&
                                    row.original.webAccess &&
                                    isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Re-invite Employees"])
                                  ) {
                                    dropdownOptions.push(
                                      <DropdownMenuItem
                                        key="reinvite"
                                        onClick={() => {
                                          navigate(`/home/employees/action/re-invite/${row.original?.empExtId}`, { replace: true });
                                          openModal("re-invite", { employeeData: row.original, afterCall: onClickFetch });
                                        }}
                                        className="cursor-pointer px-3 pr-5 py-2 text-13 text-text-default data-[highlighted]:bg-primaryOpacity-10 data-[highlighted]:text-text-primary focus:bg-primaryOpacity-10 focus:text-text-primary"
                                      >
                                        Reinvite
                                      </DropdownMenuItem>
                                    );
                                  }
                                  if (!isAccountOwnerRole) {
                                    dropdownOptions.push(
                                      <DropdownMenuItem
                                        key="share-link"
                                        className="cursor-pointer px-3 pr-5 py-2 text-13 text-text-default data-[highlighted]:bg-primaryOpacity-10 data-[highlighted]:text-text-primary focus:bg-primaryOpacity-10 focus:text-text-primary"
                                        onClick={() => {
                                          navigate(`/home/employees/action/share-link/${row.original?.empExtId}`, { replace: true });
                                          openModal("share-link", { 
                                            empId: row.original?.empExtId, 
                                            afterCall: onClickFetch,
                                            closeModal: handleShareLinkClose 
                                          });
                                        }}
                                      >
                                        Share details via link
                                      </DropdownMenuItem>
                                    );
                                  }
                                  if (
                                    row.original.status.label != "Ex-employee" &&
                                    row.original?.userTyp?.label != "Account Owner" &&
                                    isHasPermission(currentUser?.perms, PERMISSIONS.EMPLOYEE["Web Access"])
                                  ) {
                                    dropdownOptions.push(
                                      <DropdownMenuItem
                                        key="web-access"
                                        className="cursor-pointer px-3 pr-5 py-2 text-13 text-text-default data-[highlighted]:bg-primaryOpacity-10 data-[highlighted]:text-text-primary focus:bg-primaryOpacity-10 focus:text-text-primary"
                                        onClick={() => {
                                          openModal("webaccess", {
                                            data: row.original,
                                            selectionType: "single",
                                            afterCall: onClickFetch,
                                            mode: row.original.webAccess == true ? "revoke" : "grant",
                                          });
                                        }}
                                      >
                                        {row.original.webAccess == true ? "Disable web access" : "Enable web access"}
                                      </DropdownMenuItem>
                                    );
                                  }
                                  if (dropdownOptions.length === 0) return null;
                                  return (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="h-8 w-8 p-0 outline-none">
                                          <MoreHorizontal className="h-4 w-4 text-text-default" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="start">{dropdownOptions}</DropdownMenuContent>
                                    </DropdownMenu>
                                  );
                                })()}
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil((count ? count : 0) / perPage.value)}
          perPage={perPage}
          perPageOptions={[10, 25, 50, 100]}
          onPageChange={(page: any) => {
            dispatch(setPagination({ pageSize: perPage.value, pageNumber: page }));
            setTimeout(() => onClickFetch(), 200);
          }}
          onPerPageChange={(count: any) => {
            dispatch(setPagination({ pageSize: count.value, pageNumber: 1 }));
            setTimeout(() => onClickFetch(), 200);
          }}
          className=" rounded-llg rounded-t-none"
        />
      </div>
    </div>
  );
};
export default TableView;
